import { Shield, Lock, Eye, Database, UserCheck, Globe, Mail } from "lucide-react";

export default function PrivacyPage() {
  const sections = [
    {
      id: "introduction",
      title: "1. Introduction",
      content: `PlacementOS ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Platform. By using our Service, you agree to the collection and use of information in accordance with this Policy.`
    },
    {
      id: "definitions",
      title: "2. Definitions and Key Terms",
      content: `<strong>Personal Data:</strong> Information that allows identification of a natural person, including name, email, phone number, resume content, and employment history.<br/><br/>
      <strong>Sensitive Personal Data:</strong> Data revealing race, ethnicity, political opinions, religious beliefs, health information, or biometric data. We do not intentionally collect such data unless with your explicit consent.<br/><br/>
      <strong>Usage Data:</strong> Automatically collected data such as IP address, browser type, pages viewed, and click patterns.<br/><br/>
      <strong>Cookies:</strong> Small data files stored on your device to identify your browser, provide analytics, and remember your preferences.<br/><br/>
      <strong>AI Processing:</strong> Automated analysis by our artificial intelligence systems for resume evaluation, job matching, and skill assessment.`
    },
    {
      id: "collection",
      title: "3. Information We Collect",
      content: `<strong>3.1 Information You Provide Directly:</strong><br/>
      • Account Registration Data (name, email, password, phone number)<br/>
      • Resume and Career Data (work experience, education, skills, certifications)<br/>
      • Job Preferences (target roles, salary expectations, location preferences)<br/>
      • Skill Assessment Data (test results, learning progress)<br/>
      • Payment Information (processed by PCI-compliant payment processors)<br/>
      • Communications (messages, feedback, support requests)<br/><br/>
      
      <strong>3.2 Information Collected Automatically:</strong><br/>
      • Device Information (device type, operating system, browser)<br/>
      • Log Data (IP address, access times, pages viewed)<br/>
      • Usage Data (features used, actions performed on the platform)<br/>
      • Location Data (approximate location derived from IP address)<br/>
      • Cookie and Tracking Data<br/><br/>
      
      <strong>3.3 Information from Third Parties:</strong><br/>
      • Social Media Platforms (if you link your accounts for login)<br/>
      • Job Board Partners<br/>
      • Analytics Providers<br/>
      • Fraud Prevention Services`
    },
    {
      id: "usage",
      title: "4. How We Use Your Information",
      content: `<strong>4.1 Providing Our Core Services:</strong><br/>
      • Resume Optimization (ATS-friendly recommendations)<br/>
      • Skill Roadmap Generation (personalized learning pathways)<br/>
      • Job Matching (suggesting relevant opportunities)<br/>
      • Account Management and Customer Support<br/><br/>
      
      <strong>4.2 Personalization and Analytics:</strong><br/>
      • Tailoring content and recommendations to your profile<br/>
      • Understanding usage patterns for service improvement<br/>
      • Developing new features using anonymized data<br/><br/>
      
      <strong>4.3 Communications:</strong><br/>
      • Service notifications and updates<br/>
      • Job alerts and career opportunities<br/>
      • Promotional offers (with your consent)<br/><br/>
      
      <strong>4.4 Legal and Safety Purposes:</strong><br/>
      • Complying with applicable laws and regulations<br/>
      • Enforcing our Terms of Service<br/>
      • Preventing fraud and protecting user safety`
    },
    {
      id: "ai-processing",
      title: "5. AI Processing and Automated Decision-Making",
      content: `<strong>5.1 How Our AI Processes Your Data:</strong><br/>
      • Resume Analysis: Extracting key information and comparing against ATS requirements<br/>
      • Skill Gap Analysis: Identifying areas for improvement based on job requirements<br/>
      • Job Matching: Evaluating compatibility factors between your profile and opportunities<br/><br/>
      
      <strong>5.2 AI Training:</strong><br/>
      We use anonymized and aggregated data to improve our AI models. We do not use Google API data for AI model development.<br/><br/>
      
      <strong>5.3 Your Rights:</strong><br/>
      You have the right to request human review of any automated decisions that significantly affect you. Contact us at support@placementos.ai.`
    },
    {
      id: "sharing",
      title: "6. How We Share Your Information",
      content: `<strong>6.1 With Your Consent:</strong> We may share information when you explicitly authorize us.<br/><br/>
      <strong>6.2 Service Providers:</strong> Third-party vendors for hosting, payments, analytics, and customer support.<br/><br/>
      <strong>6.3 Job Matching:</strong> With prospective employers only when you choose to apply or share your profile.<br/><br/>
      <strong>6.4 Legal Requirements:</strong> When required by law or to protect our rights and safety.<br/><br/>
      <strong>6.5 Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales.<br/><br/>
      <strong>What We Do NOT Do:</strong><br/>
      • We do NOT sell your personal data<br/>
      • We do NOT share your resume without your consent<br/>
      • We do NOT use your data for unrelated advertising purposes`
    },
    {
      id: "retention",
      title: "7. Data Retention",
      content: `We retain your data for as long as your account is active or as needed to provide services:<br/><br/>
      • Active Account Data: Duration of your account plus 2 years<br/>
      • Resume Data: Until you delete it or close your account<br/>
      • Payment Records: 7 years (as required by financial regulations)<br/>
      • Usage Data: 2 years<br/>
      • Communication Records: 3 years<br/><br/>
      You can request deletion of your data at any time by contacting support@placementos.ai.`
    },
    {
      id: "security",
      title: "8. Data Security",
      content: `We implement industry-standard security measures:<br/><br/>
      • <strong>Encryption:</strong> TLS 1.3 for data in transit, AES-256 for data at rest<br/>
      • <strong>Access Controls:</strong> Role-based access with multi-factor authentication<br/>
      • <strong>Infrastructure:</strong> SOC 2 Type II compliant data centers<br/>
      • <strong>Payment Security:</strong> PCI-DSS compliant payment processing<br/><br/>
      No method of transmission over the internet is 100% secure. We strive to protect your data but cannot guarantee absolute security.`
    },
    {
      id: "rights",
      title: "9. Your Privacy Rights",
      content: `Depending on your location, you may have the following rights:<br/><br/>
      • <strong>Access:</strong> Request a copy of your personal data<br/>
      • <strong>Rectification:</strong> Correct inaccurate or incomplete data<br/>
      • <strong>Erasure:</strong> Request deletion of your data<br/>
      • <strong>Restrict Processing:</strong> Limit how we use your data<br/>
      • <strong>Data Portability:</strong> Receive your data in a portable format<br/>
      • <strong>Object:</strong> Opt out of certain processing activities<br/>
      • <strong>Withdraw Consent:</strong> Revoke previously given consent<br/><br/>
      To exercise these rights, contact us at support@placementos.ai.`
    },
    {
      id: "indian-laws",
      title: "10. Compliance with Indian Data Protection Laws",
      content: `We comply with India's Digital Personal Data Protection Act, 2023 (DPDPA) and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.`
    },
    {
      id: "children",
      title: "11. Children's Privacy",
      content: `Our Platform is intended for users 18 years and older. We do not knowingly collect personal data from children under 18. If you believe a child has provided us with personal data, please contact us immediately.`
    },
    {
      id: "changes",
      title: "12. Changes to This Privacy Policy",
      content: `We may update this Privacy Policy from time to time. We will notify you of any material changes by:<br/><br/>
      • Posting the updated policy on our Platform<br/>
      • Sending an email notification<br/>
      • Displaying an in-app notification<br/><br/>
      Your continued use of our Service after changes become effective constitutes acceptance of the revised policy.`
    },
    {
      id: "contact",
      title: "13. Contact Information",
      content: `<strong>Privacy Team:</strong><br/>
      Email: support@placementos.ai<br/>
      Response Time: Within 30 days<br/><br/>
      <strong>Grievance Officer:</strong><br/>
      Email: support@placementos.ai<br/>
      Response Time: Within 48 hours<br/>
      Resolution Time: Within 30 days`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-28 pb-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#2832C2]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[#2832C2]" />
          </div>
          <h1 className="text-4xl font-bold text-[#0A1128] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            Last Updated: February 12, 2026
          </p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 px-6 border-b border-gray-200 bg-white sticky top-[72px] z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            {[
              { icon: Database, label: "Data Collection", href: "#collection" },
              { icon: Eye, label: "How We Use Data", href: "#usage" },
              { icon: Lock, label: "Security", href: "#security" },
              { icon: UserCheck, label: "Your Rights", href: "#rights" }
            ].map((item, i) => (
              <a
                key={i}
                href={item.href}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600 hover:bg-[#2832C2]/10 hover:text-[#2832C2] transition-all"
              >
                <item.icon className="w-4 h-4" /> {item.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto space-y-12">
          {sections.map((section) => (
            <div key={section.id} id={section.id} className="scroll-mt-40">
              <h2 className="text-xl font-bold text-[#0A1128] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {section.title}
              </h2>
              <div 
                className="text-gray-600 leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Contact Banner */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <Mail className="w-10 h-10 text-[#2832C2] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-[#0A1128] mb-2">Questions about your privacy?</h3>
          <p className="text-gray-600 mb-4">Contact our Privacy Team at</p>
          <a href="mailto:support@placementos.ai" className="text-[#2832C2] font-semibold hover:underline text-lg">
            support@placementos.ai
          </a>
        </div>
      </section>
    </div>
  );
}
